// services/notificationService.ts
import { supabase } from '@/lib/supabaseClient';

interface NotificationData {
  type: 'new_lead' | 'hot_lead' | 'chat_request' | 'offer_request';
  title: string;
  message: string;
  data: any;
  priority: 'low' | 'medium' | 'high' | 'urgent';
}

export const notificationService = {
  // Wyślij powiadomienie email
  async sendEmailNotification(to: string[], notification: NotificationData) {
    try {
      const response = await fetch('https://plexisystem-backend.onrender.com/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: to.join(','),
          subject: `🔔 ${notification.title}`,
          html: this.generateEmailTemplate(notification),
        }),
      });

      if (!response.ok) throw new Error('Failed to send email');
      
      return await response.json();
    } catch (error) {
      console.error('Error sending email notification:', error);
      throw error;
    }
  },

  // Zapisz powiadomienie w bazie
  async createNotification(userId: string, notification: NotificationData) {
    const { data, error } = await supabase
      .from('notifications')
      .insert({
        user_id: userId,
        type: notification.type,
        title: notification.title,
        message: notification.message,
        data: notification.data,
        read: false,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Wyślij powiadomienie real-time przez Supabase
  async broadcastNotification(channel: string, notification: NotificationData) {
    const { error } = await supabase
      .channel(channel)
      .send({
        type: 'broadcast',
        event: 'new_notification',
        payload: notification,
      });

    if (error) throw error;
  },

  // Powiadom o nowym leadzie
  async notifyNewLead(leadData: any, conversationId: string) {
    const notification: NotificationData = {
      type: 'new_lead',
      title: 'Nowy Lead z Czatbota!',
      message: `${leadData.contact_name} z ${leadData.company_name || 'Nieznana firma'} rozpoczął rozmowę`,
      data: {
        leadId: leadData.id,
        conversationId,
        leadData,
      },
      priority: 'high',
    };

    // Pobierz listę handlowców do powiadomienia
    // TODO: Zmienić na właściwą tabelę z handlowcami
    // const { data: salespeople } = await supabase
    //   .from('users')
    //   .select('id, email')
    //   .eq('role', 'salesperson')
    //   .eq('active', true);

    // Tymczasowo użyj stałych emailów
    const salespeople = [
      { id: '1', email: 'lukasz@plexisystem.pl' },
      { id: '2', email: 'biuro@plexisystem.pl' }
    ];

    if (salespeople && salespeople.length > 0) {
      // Wyślij emaile
      const emails = salespeople.map(sp => sp.email).filter(Boolean);
      if (emails.length > 0) {
        await this.sendEmailNotification(emails, notification);
      }

      // Zapisz powiadomienia w bazie (pomiń na razie)
      // for (const salesperson of salespeople) {
      //   await this.createNotification(salesperson.id, notification);
      // }

      // Broadcast real-time
      await this.broadcastNotification('sales-notifications', notification);
    }
  },

  // Powiadom o gorącym leadzie (np. pytanie o cenę)
  async notifyHotLead(leadData: any, message: string) {
    const notification: NotificationData = {
      type: 'hot_lead',
      title: '🔥 Gorący Lead - Zainteresowanie Zakupem!',
      message: `${leadData.contact_name} pyta o: "${message}"`,
      data: {
        leadId: leadData.id,
        message,
        leadData,
      },
      priority: 'urgent',
    };

    // Natychmiastowe powiadomienie
    await this.broadcastNotification('urgent-notifications', notification);
    
    // Email do kierownika sprzedaży
    // const { data: managers } = await supabase
    //   .from('users')
    //   .select('email')
    //   .eq('role', 'sales_manager')
    //   .single();

    // if (managers?.email) {
    //   await this.sendEmailNotification([managers.email], notification);
    // }
    
    // Tymczasowo wyślij na stały email
    await this.sendEmailNotification(['lukasz@plexisystem.pl'], notification);
  },

  // Szablon emaila
  generateEmailTemplate(notification: NotificationData): string {
    const priorityColors = {
      low: '#6b7280',
      medium: '#3b82f6',
      high: '#f59e0b',
      urgent: '#ef4444',
    };

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; background-color: #f3f4f6; margin: 0; padding: 20px; }
          .container { max-width: 600px; margin: 0 auto; background-color: white; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
          .header { background-color: #1f2937; color: white; padding: 20px; text-align: center; }
          .priority-badge { display: inline-block; padding: 4px 12px; border-radius: 4px; font-size: 12px; font-weight: bold; background-color: ${priorityColors[notification.priority]}; color: white; }
          .content { padding: 30px; }
          .lead-info { background-color: #f9fafb; padding: 20px; border-radius: 8px; margin: 20px 0; }
          .lead-info h3 { margin-top: 0; color: #1f2937; }
          .lead-info p { margin: 8px 0; color: #4b5563; }
          .cta-button { display: inline-block; padding: 12px 24px; background-color: #f59e0b; color: white; text-decoration: none; border-radius: 6px; font-weight: bold; margin-top: 20px; }
          .footer { padding: 20px; text-align: center; color: #6b7280; font-size: 14px; border-top: 1px solid #e5e7eb; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1 style="margin: 0; font-size: 24px;">PlexiSystem CRM</h1>
            <p style="margin: 10px 0 0 0; opacity: 0.9;">${notification.title}</p>
          </div>
          
          <div class="content">
            <div style="text-align: center; margin-bottom: 20px;">
              <span class="priority-badge">${notification.priority.toUpperCase()}</span>
            </div>
            
            <p style="font-size: 16px; color: #374151; line-height: 1.6;">
              ${notification.message}
            </p>
            
            ${notification.data.leadData ? `
              <div class="lead-info">
                <h3>Dane kontaktowe:</h3>
                <p><strong>Imię:</strong> ${notification.data.leadData.contact_name || 'Nie podano'}</p>
                <p><strong>Firma:</strong> ${notification.data.leadData.company_name || 'Nie podano'}</p>
                <p><strong>Email:</strong> ${notification.data.leadData.email || 'Nie podano'}</p>
                <p><strong>Telefon:</strong> ${notification.data.leadData.phone || 'Nie podano'}</p>
                ${notification.data.leadData.notes ? `<p><strong>Wiadomość:</strong> ${notification.data.leadData.notes}</p>` : ''}
              </div>
            ` : ''}
            
            <div style="text-align: center;">
              <a href="http://localhost:5173/crm/leads/${notification.data.leadId}" class="cta-button">
                Zobacz w CRM
              </a>
            </div>
          </div>
          
          <div class="footer">
            <p>To powiadomienie zostało wygenerowane automatycznie przez PlexiSystem.</p>
            <p>© 2024 PlexiSystem. Wszystkie prawa zastrzeżone.</p>
          </div>
        </div>
      </body>
      </html>
    `;
  },
};